/**
 * Configuration i18n partagée DooDates (react-i18next).
 *
 * ARCHITECTURE : `@core` est un alias *source* — chaque app Vite bundle ce
 * fichier à neuf, donc il existe UNE instance i18next PAR app (pas de singleton
 * cross-app). C'est voulu : aucun couplage runtime entre apps.
 *
 * - Namespaces *partagés* déclarés ici : common, auth, errors, legal,
 *   creator, empty_state, admin, ai_poll (codes et messages du parcours IA :
 *   serveur MCP et lien pré-rempli `/new`, cf. `lib/ai-poll/`).
 * - Les namespaces *produit* (ex: `polls`) sont enregistrés par chaque app via
 *   `registerNamespace()` pour éviter que toutes les apps bundlent les strings
 *   des autres.
 * - Init synchrone (resources inline), pas de http-backend pour 2 langues.
 * - Guard `isInitialized` contre la double-init.
 */
import i18n, { type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import frCommon from "./locales/fr/common.json";
import enCommon from "./locales/en/common.json";
import frAuth from "./locales/fr/auth.json";
import enAuth from "./locales/en/auth.json";
import frErrors from "./locales/fr/errors.json";
import enErrors from "./locales/en/errors.json";
import frLegal from "./locales/fr/legal.json";
import enLegal from "./locales/en/legal.json";
import frCreator from "./locales/fr/creator.json";
import enCreator from "./locales/en/creator.json";
import frEmptyState from "./locales/fr/empty_state.json";
import enEmptyState from "./locales/en/empty_state.json";
import esCommon from "./locales/es/common.json";
import esAuth from "./locales/es/auth.json";
import esErrors from "./locales/es/errors.json";
import esLegal from "./locales/es/legal.json";
import esCreator from "./locales/es/creator.json";
import esEmptyState from "./locales/es/empty_state.json";
import frAdmin from "./locales/fr/admin.json";
import enAdmin from "./locales/en/admin.json";
import esAdmin from "./locales/es/admin.json";
import frAiPoll from "./locales/fr/ai_poll.json";
import enAiPoll from "./locales/en/ai_poll.json";
import esAiPoll from "./locales/es/ai_poll.json";

export type AppLocale = "fr" | "en" | "es";

export const SUPPORTED_LOCALES: AppLocale[] = ["fr", "en", "es"];
export const DEFAULT_LOCALE: AppLocale = "en";
export const STORAGE_KEY = "doodates_lang";
/**
 * Cookie posé à chaque choix EXPLICITE de langue (sélecteur, préférence de
 * compte, `?lang=`) — jamais par la détection automatique. Lu par
 * middleware.ts (le serveur ne voit pas localStorage) : il prime sur
 * Accept-Language pour décider de rediriger vers /en/….
 */
export const LANG_COOKIE = "doodates_lang";

/** Tag Intl/BCP-47 pour une locale app (formatage dates/nombres/devise). */
export function localeTag(locale: string = i18n.language): string {
  if (locale.startsWith("en")) return "en-US";
  if (locale.startsWith("es")) return "es-ES";
  return "fr-FR";
}

const resources: Resource = {
  fr: {
    common: frCommon,
    auth: frAuth,
    errors: frErrors,
    legal: frLegal,
    creator: frCreator,
    empty_state: frEmptyState,
    admin: frAdmin,
    ai_poll: frAiPoll,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    errors: enErrors,
    legal: enLegal,
    creator: enCreator,
    empty_state: enEmptyState,
    admin: enAdmin,
    ai_poll: enAiPoll,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    errors: esErrors,
    legal: esLegal,
    creator: esCreator,
    empty_state: esEmptyState,
    admin: esAdmin,
    ai_poll: esAiPoll,
  },
};

/**
 * Migration (une seule fois par navigateur) : un choix de langue fait avant
 * l'existence du cookie n'est que dans localStorage, que le serveur ne voit
 * pas. On le recopie dans le cookie {@link LANG_COOKIE} pour que
 * middleware.ts le respecte, sans écraser un cookie déjà présent.
 *
 * Appelée AVANT i18n.init : le détecteur réécrit ensuite localStorage avec
 * la langue DÉTECTÉE ; la lire après reviendrait à promouvoir une détection
 * en choix. Le marqueur évite qu'une visite ultérieure d'une page /en/…
 * (qui change la langue sans la choisir) soit recopiée à son tour.
 */
export const LANG_MIGRATION_FLAG = "doodates_lang_cookie_migrated";

export function migrateStoredLangToCookie(): void {
  if (typeof document === "undefined") return;
  try {
    if (localStorage.getItem(LANG_MIGRATION_FLAG)) return;
    const lang = localStorage.getItem(STORAGE_KEY)?.slice(0, 2);
    const hasCookie = document.cookie
      .split(";")
      .some((c) => c.trim().startsWith(`${LANG_COOKIE}=`));
    if (lang && SUPPORTED_LOCALES.includes(lang as AppLocale) && !hasCookie) {
      document.cookie = `${LANG_COOKIE}=${lang}; path=/; max-age=31536000; SameSite=Lax`;
    }
    localStorage.setItem(LANG_MIGRATION_FLAG, "1");
  } catch {
    // Stockage indisponible (navigation privée stricte) : rien à migrer.
  }
}

if (!i18n.isInitialized) {
  migrateStoredLangToCookie();
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: SUPPORTED_LOCALES,
      nonExplicitSupportedLngs: true,
      defaultNS: "common",
      ns: [
        "common",
        "auth",
        "errors",
        "legal",
        "creator",
        "empty_state",
        "admin",
        "ai_poll",
      ],
      detection: {
        // querystring d'abord : les liens hreflang émis par SEOHead pointent
        // vers ?lang=xx — ils doivent réellement basculer la langue.
        order: ["querystring", "localStorage", "navigator"],
        lookupQuerystring: "lang",
        lookupLocalStorage: STORAGE_KEY,
        caches: ["localStorage"],
      },
      interpolation: { escapeValue: false },
      // Rendu sûr pendant la migration incrémentale : namespaces partiellement
      // vides → renvoie la clé (pas de crash, pas de Suspense).
      react: { useSuspense: false },
      returnEmptyString: false,
    });
}

/** Enregistre un namespace produit (appelé par chaque app). */
export function registerNamespace(
  ns: string,
  bundles: Partial<Record<AppLocale, Record<string, unknown>>>,
): void {
  for (const locale of SUPPORTED_LOCALES) {
    const bundle = bundles[locale];
    if (bundle) i18n.addResourceBundle(locale, ns, bundle, true, true);
  }
}

/** Choix explicite de langue : change la langue active, met à jour
 * <html lang> et pose le cookie {@link LANG_COOKIE}. */
export function setLocale(locale: AppLocale): Promise<unknown> {
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
    document.cookie = `${LANG_COOKIE}=${locale}; path=/; max-age=31536000; SameSite=Lax`;
  }
  return i18n.changeLanguage(locale);
}

// Garder <html lang> synchro avec la langue active.
if (typeof document !== "undefined") {
  document.documentElement.lang = (i18n.language || DEFAULT_LOCALE).slice(0, 2);
  i18n.on("languageChanged", (lng) => {
    document.documentElement.lang = lng.slice(0, 2);
  });
}

// Détect missing translation keys at runtime (raw keys on screen)
if (typeof window !== "undefined") {
  i18n.on("missingKey", (lng, ns, key) => {
    const fullKey = `${ns}.${key}`;
    console.error(`❌ i18n missing key: ${fullKey} (lang: ${lng})`);
    // In CI/test, fail loud
    if (typeof process !== "undefined" && process.env?.CI) {
      throw new Error(`Missing i18n key: ${fullKey}`);
    }
  });
}

export default i18n;
